import type {
    Experiment,
    Pointers,
    SchemaService,
    UndoRedoConfig,
    Events,
    ValidationService,
    SupportedFragments
} from '@wix/document-services-types'
import {EventEmitter} from 'events'
import _ from 'lodash'
import {createDal, CustomGetter} from './dal/documentManagerDal'
import type {DAL} from './dal/baseDal'
import {createStore} from './dal/store'
import {createResourceLoader, ResourceLoaderAPI} from './resourceLoader'
import type {
    CoreLogger,
    DmApis,
    DMApisWithEvents,
    DocumentDataTypes,
    Extension,
    ExtensionAPI,
    ExtensionPublicAPI,
    PostSetOperation
} from './types'
import {getPointer} from './utils/pointerUtils'
import {createFragmentDalManager} from './dal/fragment/fragmentDalManager'
import {createFragmentStore} from './dal/fragmentStore'
import type {FragmentListItem} from '@wix/document-services-types/src/documentServicesModel'

const assignWithoutDuplicates = (target: Record<string, any>, sources: Record<string, any>, type: string) => {
    _.forOwn(sources, (value, key) => {
        if (target[key]) {
            throw new Error(`Trying to add an existing "${type}" with key "${key}"`)
        }

        target[key] = value
    })
}

export interface CoreConfig {
    experimentInstance: Experiment
    logger: CoreLogger
    tracking?: Function
    schemaService: SchemaService
    validationService?: ValidationService
    transactionActionsLimit: number
    signatureSeed?: string
    undoRedoConfig?: UndoRedoConfig
    disableCommonConfig?: boolean
    dontCollectFixerVersionData?: boolean
    supportsUsingPresetVariants?: boolean
    minNumOfPagesToCalculateRenderHints: number
    enableWidgetInRepeater?: boolean
    supportedFragments?: SupportedFragments
    withoutViewer?: boolean
    enforceStrictValidationEverywhere?: boolean
    enforceNoWhitelistDalStrictSchemaValidation?: boolean
    strictBlacklistBaselineForEmployees?: boolean
    loadingPartialPages?: boolean
}

export interface ModelsInitializationType {
    rendererModel: any
    documentServicesModel: any & {
        fragmentsList?: FragmentListItem[]
    }
    serviceTopology: any
}
export interface DocumentManager {
    dal: DAL
    resourceLoader: ResourceLoaderAPI
    pointers: Pointers
    extensionAPI: ExtensionAPI
    publicAPI: ExtensionPublicAPI
    EVENTS: Record<string, any>
    eventEmitter: EventEmitter
    events: Events
    registerExtension(ext: Extension): void
    initialize(): Promise<void>
    initializeChannelSubscriptions(): Promise<void>
    initializeModels(initData: ModelsInitializationType): void
    experimentInstance: Experiment
    logger: CoreLogger
    schemaService: any
    config: CoreConfig
}

export const createDMCore = (coreConfig: CoreConfig): DocumentManager => {
    const {experimentInstance, logger, schemaService} = coreConfig
    const extensions: Extension[] = []
    const postSetOperations: PostSetOperation[] = []
    const customGetters: Record<string, CustomGetter> = {}
    const dmTypes: DocumentDataTypes = {}
    const pointers: Pointers = {} as Pointers
    const extensionAPI: ExtensionAPI = {}
    const publicAPI: ExtensionPublicAPI = {}
    const eventEmitter: EventEmitter = new EventEmitter()
    const EVENTS = {}
    const events: Events = {
        emitter: eventEmitter
    }
    const tracking = coreConfig.tracking ?? (async (name: string, fn: Function) => await fn())
    const resourceLoader = createResourceLoader(coreConfig)
    const dalArgs = {coreConfig, postSetOperations, dmTypes, customGetters, eventEmitter}
    const dal: DAL = experimentInstance.isOpen('dm_fragments') ? createFragmentDalManager(dalArgs) : createDal(dalArgs)
    const dmApis: DmApis = {coreConfig, dal, pointers, extensionAPI, EVENTS, resourceLoader}
    const dmAPIsWithEvents: DMApisWithEvents = {...dmApis, eventEmitter}
    const validate = (extension: Extension): void => {
        if (extension.name) {
            if (_(extensions).map('name').includes(extension.name)) {
                throw new Error(`extension name should be unique ${extension.name}`)
            }
        } else {
            throw new Error('extension should have a name')
        }
    }

    const registerExtension = (extension: Extension): void => {
        validate(extension)
        extensions.push(extension)

        assignWithoutDuplicates(customGetters, _.invoke(extension, ['createGetters'], dmApis), 'customGetter')
        _.merge(dmTypes, _.invoke(extension, ['getDocumentDataTypes'], dmApis))
        _.merge(pointers, _.invoke(extension, ['createPointersMethods'], dmApis))
        _.merge(extensionAPI, _.invoke(extension, ['createExtensionAPI'], dmAPIsWithEvents))
        _.merge(publicAPI, _.invoke(extension, ['createPublicAPI'], dmAPIsWithEvents))
        _.merge(EVENTS, extension.EVENTS)
        _.merge(events, extension.EVENTS)

        const filters = _.invoke(extension, ['createFilters'], dmApis)
        _.forEach(filters, (filter, name) => dal.registrar.registerFilter(name, filter))

        const validators = _.invoke(extension, ['createValidator'], dmApis)
        _.forEach(validators, (validator, name) => dal.registrar.registerValidator(name, validator))

        const rebaseValidators = _.invoke(extension, ['createRebaseValidator'], dmApis)
        _.forEach(rebaseValidators, (validator, name) => dal.registrar.registerRebaseValidator(name, validator))

        const postTransactionOperations = _.invoke(extension, ['createPostTransactionOperations'], {
            ...dmApis,
            eventEmitter
        })
        _.forEach(postTransactionOperations, (op, name) => dal.registrar.registerPostTransactionOperation(name, op))

        const transactionDerivedMetadata = _.invoke(extension, ['createTransactionDerivedMetadata'], {
            ...dmApis,
            eventEmitter
        })

        if (transactionDerivedMetadata) {
            _.forEach(transactionDerivedMetadata, ([op, fragmentType], name) =>
                dal.registrar.registerTransactionDerivedMetadata(name, op, fragmentType)
            )
        }

        const changesListener = _.invoke(extension, ['createChangesListener'], dmApis)
        if (changesListener) {
            dal.registrar.registerForChangesCallback(changesListener)
        }

        if (extension.createPostSetOperation) {
            postSetOperations.push(extension.createPostSetOperation(dmApis))
        }

        if (extension.createResourceLoader) {
            const resourceLoaderDefinitions = extension.createResourceLoader(dmAPIsWithEvents)
            _.forEach(resourceLoaderDefinitions, loaderDefinition =>
                resourceLoader.registerResourceLoader(loaderDefinition)
            )
        }
        if (extension.initialState) {
            const initialTypeState = experimentInstance.isOpen('dm_fragments') ? createFragmentStore() : createStore()
            _.forEach(extension.initialState, (state, type) => {
                _.forEach(state, (value, key) => {
                    initialTypeState.set(getPointer(key, type), value)
                })
            })
            dal.mergeToApprovedStore(initialTypeState, extension.name)
        }

        if (extension.subscribeToEvents) {
            extension.subscribeToEvents(dmAPIsWithEvents)
        }
    }
    const invokeAsyncOnExtensionsAndTrack = (
        fnName: keyof Extension,
        extensionArgs: any[],
        invocationCallback?: (extensionName: string) => void
    ): any[] =>
        extensions
            .filter(ex => ex[fnName])
            .map(ex => {
                const returnValue = tracking(`${fnName}-${ex.name}`, () => _.invoke(ex, fnName, ...extensionArgs))
                invocationCallback?.(ex.name)
                return returnValue
            })

    const invokeOnExtensions = (fnName: keyof Extension, ...args: any[]): any[] =>
        _(extensions)
            .filter(fnName)
            .map((ex: Extension) => _.invoke(ex, fnName, ...args))
            .value()

    const initialize = async () => {
        await Promise.all(
            invokeAsyncOnExtensionsAndTrack('initialize', [dmAPIsWithEvents], extensionName => {
                dal.safeCommitTransactionWithErrorReport(
                    {
                        failedValidationAfterEarlyBaseline: true,
                        phase: 'extensions',
                        extensionName
                    },
                    `after-${extensionName}`
                )
            })
        )
    }

    const initializeChannelSubscriptions = async () => {
        await Promise.all(invokeAsyncOnExtensionsAndTrack('initializeChannelSubscriptions', [dmAPIsWithEvents]))
    }
    const initializeModels = (initModelsData: ModelsInitializationType) => {
        const initialModelsState = experimentInstance.isOpen('dm_fragments') ? createFragmentStore() : createStore()
        invokeOnExtensions('initializeModels', initialModelsState, initModelsData, dmAPIsWithEvents)
        dal.mergeToApprovedStore(initialModelsState, 'model-initializer')
    }

    return {
        registerExtension,
        initialize,
        initializeModels,
        initializeChannelSubscriptions,
        resourceLoader,
        extensionAPI,
        publicAPI,
        dal,
        eventEmitter,
        EVENTS,
        events,
        pointers,
        experimentInstance,
        logger,
        schemaService,
        config: coreConfig
    }
}
